import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import EmbedContext from "../../context/Embed";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useTranslation } from "react-i18next";
import Logo from "../../assets/images/logo.png";

function Footer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down("md"));
  const { isEmbed } = useContext(EmbedContext);

  const redirectHandler = (link) => {
    window.open(link, "_blank");
  };

  // Embed mode (partner iframe pinned to one restaurant): the footer is all
  // discovery/marketing links — hide it entirely.
  if (isEmbed) return null;

  const handleButtonClick = () => {
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      window.scrollTo(0, 0);
    }
  };

  const footerLinks = [
    { title: t("footerLinkHome"), link: "/" },
    { title: t("footerLinkPP"), link: "/privacy" },
    { title: t("footerLinkTC"), link: "/terms" },
    { title: "Become a Partner", link: "/partner-signup" },
    // Another site, so a plain <a>; also the link Google follows to find the admin.
    { title: "Restaurant Login", link: "https://admin.rushmyfood.com/", external: true },
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, url: "https://www.facebook.com/RushMyFood/" },
    { icon: <InstagramIcon />, url: "https://www.instagram.com/rushmyfood/" },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#1A1A1A",
        padding: small ? "48px 24px" : "60px 48px",
        width: "100%",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and Description */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <img
                src={Logo}
                alt="RushmyFood"
                style={{
                  height: 48,
                  objectFit: "contain",
                  marginBottom: 16,
                }}
              />
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "0.938rem",
                  lineHeight: 1.7,
                  maxWidth: 300,
                }}
              >
                {t("footerText")}
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "1.125rem",
                mb: 2,
              }}
            >
              {t("linksTitle")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {footerLinks.map((item, index) => {
                const label = (
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.938rem",
                      transition: "color 0.2s ease",
                      "&:hover": {
                        color: "#FF3008",
                      },
                    }}
                  >
                    {item.title}
                  </Typography>
                );
                return item.external ? (
                  <a key={index} href={item.link} style={{ textDecoration: "none" }}>
                    {label}
                  </a>
                ) : (
                  <RouterLink
                    key={index}
                    to={item.link}
                    onClick={handleButtonClick}
                    style={{ textDecoration: "none" }}
                  >
                    {label}
                  </RouterLink>
                );
              })}
            </Box>
          </Grid>

          {/* Social Links */}
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "1.125rem",
                mb: 2,
              }}
            >
              {t("followUs")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {socialLinks.map((social, index) => (
                <Box
                  key={index}
                  onClick={() => redirectHandler(social.url)}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "& svg": {
                      color: "rgba(255,255,255,0.7)",
                      fontSize: 20,
                    },
                    "&:hover": {
                      backgroundColor: "#FF3008",
                      transform: "translateY(-2px)",
                      "& svg": {
                        color: "#FFFFFF",
                      },
                    },
                  }}
                >
                  {social.icon}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            mt: 5,
            pt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.875rem",
            }}
          >
            © 2026 RushMyFood. All rights reserved.
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.875rem",
            }}
          >
            {t("poweredBy")} RushmyFood
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
